// The REST door from the browser: POST /api/tools/<name>. Refusals arrive as RFC 9457 problem+json and become AppError.
import type { FileUploadInput, FileUploadOutput } from "./tools";

export interface Problem {
  type: string;
  title: string;
  status: number;
  detail: string;
  instance?: string;
  refusal_code: string;
  remedy?: string;
  request_id?: string | null;
  errors?: { field: string; message: string }[];
  reject_reason?: string;
  /** the upload door's reading of the bytes (`password_protected`), beside reject_reason */
  document_error?: string;
}

export class AppError extends Error {
  readonly status: number;
  readonly code: string;
  readonly remedy: string;
  readonly fieldErrors: Record<string, string>;
  readonly requestId: string | null;
  readonly rejectReason: string | null;
  readonly documentError: string | null;
  /** seconds from a Retry-After header (lockouts, rate limits); null when absent */
  readonly retryAfterSeconds: number | null;
  constructor(p: Problem, retryAfterSeconds: number | null = null) {
    super(p.detail || p.title);
    this.retryAfterSeconds = retryAfterSeconds;
    this.status = p.status;
    this.code = p.refusal_code;
    this.remedy = p.remedy ?? "";
    this.fieldErrors = Object.fromEntries((p.errors ?? []).map((e) => [e.field, e.message]));
    this.requestId = p.request_id ?? null;
    this.rejectReason = p.reject_reason ?? null;
    this.documentError = p.document_error ?? null;
  }
  get unauthenticated(): boolean {
    return this.status === 401;
  }
  /** the server could not be reached at all (no problem body) */
  get unreachable(): boolean {
    return this.code === "NETWORK";
  }
}

/** the instant of the last request that reached the server (the server renews the session on every call) */
/**
 * The application's clock. Under capture it is the server's business instant (`Me.business_now`), frozen when the
 * shell mounts, so a greeting, a due phrase or a "completed at" reads the same in every frame; otherwise the wall clock.
 */
let frozenNow: number | null = null;
export function freezeClock(instant: string | null | undefined): void {
  const at = instant ? Date.parse(instant) : Number.NaN;
  frozenNow = Number.isFinite(at) ? at : null;
}
export function clockNow(): number {
  return frozenNow ?? Date.now();
}

export let lastActivityAt = Date.now();
export function touchActivity(): void {
  lastActivityAt = Date.now();
}

let onUnauthenticated: (() => void) | null = null;
export function setUnauthenticatedHandler(fn: () => void): void {
  onUnauthenticated = fn;
}

async function parse<T>(res: Response): Promise<T> {
  const ct = res.headers.get("content-type") ?? "";
  if (res.ok) {
    return (ct.includes("json") ? await res.json() : (await res.text())) as T;
  }
  let problem: Problem;
  if (ct.includes("problem+json") || ct.includes("json")) {
    problem = (await res.json()) as Problem;
  } else {
    problem = { type: "about:blank", title: res.statusText, status: res.status, detail: await res.text(), refusal_code: "HTTP_" + res.status };
  }
  const ra = res.headers.get("retry-after");
  const err = new AppError(problem, ra && /^\d+$/.test(ra) ? Number(ra) : null);
  if (err.unauthenticated && onUnauthenticated) onUnauthenticated();
  throw err;
}

async function send(input: RequestInfo, init: RequestInit): Promise<Response> {
  try {
    if (["POST", "PUT", "PATCH", "DELETE"].includes(init.method ?? "GET")) {
      let token = document.cookie.split("; ").find((c) => c.startsWith("__Host-ta_csrf=") || c.startsWith("ta_csrf="))?.split("=")[1];
      if (!token) {
        const bootstrap = await fetch("/api/session/csrf", { credentials: "include" });
        token = (await parse<{ csrf_token: string }>(bootstrap)).csrf_token;
      }
      const headers = new Headers(init.headers);
      headers.set("X-CSRF-Token", token);
      init = { ...init, headers };
    }
    const res = await fetch(input, init);
    if (res.status !== 401) touchActivity();
    return res;
  } catch (err) {
    if (err instanceof AppError) throw err;
    throw new AppError({ type: "about:blank", title: "Not reachable", status: 0, detail: "The application is not reachable right now.", refusal_code: "NETWORK" });
  }
}

/** Multipart uses the same session-bound CSRF token as the JSON door. Read afresh after every session rotation. */
async function uploadToken(): Promise<string> {
  const token = document.cookie.split("; ").find((c) => c.startsWith("__Host-ta_csrf=") || c.startsWith("ta_csrf="))?.split("=")[1];
  if (token) return token;
  return (await getJson<{ csrf_token: string }>("/api/session/csrf")).csrf_token;
}

export type UploadOwner = Pick<FileUploadInput, "owner_type" | "owner_id" | "kind" | "requisition_id">;

/** XHR's upload completion is the real boundary between sending bytes and waiting for the synchronous scanner. */
export async function uploadFile(file: File, owner: UploadOwner, options: { signal?: AbortSignal; onPhase?: (phase: "uploading" | "scanning") => void } = {}): Promise<FileUploadOutput> {
  const token = await uploadToken();
  const form = new FormData();
  form.append("file", file);
  for (const [key, value] of Object.entries(owner)) if (value) form.append(key, value);
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const fail = () => reject(new AppError({ type: "about:blank", title: "Upload failed", status: 0, detail: "The file could not be uploaded. Try again.", refusal_code: "NETWORK" }));
    const abort = () => xhr.abort();
    xhr.open("POST", "/api/files");
    xhr.withCredentials = true;
    xhr.timeout = 120_000;
    xhr.setRequestHeader("X-CSRF-Token", token);
    xhr.setRequestHeader("Accept", "application/json, application/problem+json");
    xhr.upload.onload = () => options.onPhase?.("scanning");
    xhr.onerror = fail;
    xhr.ontimeout = fail;
    xhr.onabort = fail;
    xhr.onloadend = () => options.signal?.removeEventListener("abort", abort);
    xhr.onload = () => {
      if (xhr.status !== 401) touchActivity();
      const headers = new Headers();
      for (const name of ["content-type", "retry-after"]) {
        const value = xhr.getResponseHeader(name);
        if (value) headers.set(name, value);
      }
      void parse<{ result: FileUploadOutput }>(new Response(xhr.responseText, { status: xhr.status, headers })).then((body) => { publishReceipt(body.result); resolve(body.result); }, reject);
    };
    if (options.signal?.aborted) { fail(); return; }
    options.signal?.addEventListener("abort", abort, { once: true });
    options.onPhase?.("uploading");
    xhr.send(form); // never set Content-Type: the browser supplies the multipart boundary
  });
}

/** The reason envelope: one optional human reason on any call, sent as the percent-encoded UTF-8 header X-TA-Reason and
 *  recorded on the call's audit receipts only (the server trims it and refuses one outside 3-500 characters). */
export const REASON_HEADER = "X-TA-Reason";

export async function callTool<TOut>(name: string, input: unknown = {}, opts: { idempotencyKey?: string; reason?: string } = {}): Promise<TOut> {
  const headers: Record<string, string> = { "content-type": "application/json", accept: "application/json, application/problem+json" };
  if (opts.idempotencyKey) headers["Idempotency-Key"] = opts.idempotencyKey;
  const reason = opts.reason?.trim();
  if (reason) headers[REASON_HEADER] = encodeURIComponent(reason);
  const res = await send(`/api/tools/${name}`, { method: "POST", credentials: "include", headers, body: JSON.stringify(input ?? {}) });
  const body = await parse<{ ok: true; tool: string; result: TOut }>(res);
  if (opts.idempotencyKey) publishReceipt(body.result);
  return body.result;
}

export async function getJson<T>(path: string): Promise<T> {
  const res = await send(path, { credentials: "include", headers: { accept: "application/json, application/problem+json" } });
  return parse<T>(res);
}

export async function postJson<T>(path: string, body: unknown): Promise<T> {
  const res = await send(path, { method: "POST", credentials: "include", headers: { "content-type": "application/json", accept: "application/json, application/problem+json" }, body: JSON.stringify(body ?? {}) });
  return parse<T>(res);
}

export function newIdempotencyKey(): string {
  const a = new Uint8Array(16);
  crypto.getRandomValues(a);
  return Array.from(a, (b) => b.toString(16).padStart(2, "0")).join("");
}

// ---- the business time zone -------------------------------------------------------------------------------------------
// Every timestamp in the app is shown in the business calendar's zone (calendar.yaml → /api/session/me → here), never the
// viewer's — a slot at 16:00Z is "12:00 PM EDT" on every screen, wherever the reader sits. Set once after sign-in.
let businessTimeZone = "America/New_York";
export function setBusinessTimeZone(tz: string | null | undefined): void {
  if (tz) businessTimeZone = tz;
}
export function getBusinessTimeZone(): string {
  return businessTimeZone;
}

// ---- datetime-local values, in the business zone ------------------------------------------------------------------------
// A `type="datetime-local"` control has no zone: it hands back the wall clock the reader typed, and the browser would read it
// in the READER's zone while every timestamp on screen is the business calendar's (fmtDate). These three helpers make the
// control speak the business zone in both directions, so what a reader types is the wall clock they see everywhere else.
//   businessLocalNow()                       → "2026-09-09T09:00"  (this minute, as New York reads it)
//   businessLocalToISO("2026-09-09T09:00")   → "2026-09-09T13:00:00.000Z"  (09:00 typed on an EDT day is 13:00Z)
//   isoToBusinessLocal("2026-09-09T13:00Z")  → "2026-09-09T09:00"  (the inverse, for prefilling the input)
// Anything unparseable — a half-typed value, an absent instant, a zone the platform does not know — returns "" and never throws.

interface WallClock {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

const zoneFormatters = new Map<string, Intl.DateTimeFormat>();
function zoneFormatter(timeZone: string, locale: string | undefined): Intl.DateTimeFormat {
  const key = `${locale ?? ""}|${timeZone}`;
  let f = zoneFormatters.get(key);
  if (!f) {
    // gregory + latn are pinned: a reader whose locale defaults to another calendar or numbering system (th-TH, fa-IR) would
    // otherwise hand back 2569 or ۲۰۲۶ and the arithmetic below would read a wrong instant.
    f = new Intl.DateTimeFormat(locale, { timeZone, calendar: "gregory", numberingSystem: "latn", hourCycle: "h23", year: "numeric", month: "numeric", day: "numeric", hour: "numeric", minute: "numeric", second: "numeric" });
    zoneFormatters.set(key, f);
  }
  return f;
}

/** the wall clock an instant shows on in `timeZone`; null when the instant or the zone is not usable */
function wallClockIn(instant: number, timeZone: string): WallClock | null {
  for (const locale of [undefined, "en-US"] as const) {
    // the reader's own locale first; "en-US" is the fallback for an engine that ignores the pinned calendar or numbering system
    try {
      const parts = zoneFormatter(timeZone, locale).formatToParts(new Date(instant));
      const read = (type: Intl.DateTimeFormatPartTypes): number => Number(parts.find((p) => p.type === type)?.value);
      const wall = { year: read("year"), month: read("month"), day: read("day"), hour: read("hour"), minute: read("minute"), second: read("second") };
      if (Object.values(wall).every((n) => Number.isFinite(n))) return wall;
    } catch {
      return null; // an unknown zone name: the caller falls back rather than taking the screen down
    }
  }
  return null;
}

/** how far ahead of UTC `timeZone` runs at this instant, in milliseconds (negative west of Greenwich); NaN when unknown */
function zoneOffsetMs(instant: number, timeZone: string): number {
  const w = wallClockIn(instant, timeZone);
  if (!w) return Number.NaN;
  return Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second) - Math.floor(instant / 1000) * 1000;
}

const pad = (n: number, width = 2): string => String(n).padStart(width, "0");

/** this minute as a `datetime-local` value in the business zone, e.g. the max of a "when did this happen" field */
export function businessLocalNow(): string {
  return isoToBusinessLocal(new Date().toISOString());
}

/** a `datetime-local` value the reader typed as business-zone wall clock → the UTC ISO instant it names ("" when unparseable) */
export function businessLocalToISO(value: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/.exec(value ?? "");
  if (!m) return "";
  const [year, month, day, hour, minute, second] = [Number(m[1]), Number(m[2]), Number(m[3]), Number(m[4]), Number(m[5]), Number(m[6] ?? 0)];
  const wall = Date.UTC(year, month - 1, day, hour, minute, second);
  if (!Number.isFinite(wall)) return "";
  const rolled = new Date(wall);
  // a day that does not exist (2026-02-31) or an hour that does not (25:00) would roll over into another date: refuse instead
  if (rolled.getUTCFullYear() !== year || rolled.getUTCMonth() !== month - 1 || rolled.getUTCDate() !== day || rolled.getUTCHours() !== hour || rolled.getUTCMinutes() !== minute) return "";
  const zone = getBusinessTimeZone();
  // the offset depends on the instant, and the instant on the offset: correct once from the wall clock read as UTC, then again
  // from that candidate, so a day that changes offset (the DST weekends) lands on the offset actually in force at that hour.
  const firstPass = zoneOffsetMs(wall, zone);
  if (!Number.isFinite(firstPass)) return new Date(wall).toISOString(); // zone unknown: the wall clock stands as UTC rather than nothing
  const secondPass = zoneOffsetMs(wall - firstPass, zone);
  const instant = wall - (Number.isFinite(secondPass) ? secondPass : firstPass);
  return Number.isFinite(instant) ? new Date(instant).toISOString() : "";
}

/** a business day the reader picked (`YYYY-MM-DD`) → the instant a "when did this happen" field sends: null for today,
 *  which the server stamps with its own now (so an act recorded today stays after what was recorded earlier today, and
 *  is never later than the server's now), else that day's start in the business zone; null when empty or unparseable */
export function businessDayISO(day: string | null | undefined): string | null {
  if (!day || day === businessLocalNow().slice(0, 10)) return null;
  return businessLocalToISO(`${day}T00:00`) || null;
}

/** a UTC ISO instant → the `datetime-local` value that shows the same wall clock in the business zone ("" when unparseable) */
export function isoToBusinessLocal(iso: string | null | undefined): string {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "";
  const w = wallClockIn(t, getBusinessTimeZone());
  if (!w) return new Date(t - new Date(t).getTimezoneOffset() * 60_000).toISOString().slice(0, 16); // zone unknown: the reader's own clock
  return `${pad(w.year, 4)}-${pad(w.month)}-${pad(w.day)}T${pad(w.hour)}:${pad(w.minute)}`;
}

/** Publish only mutation results; a scorecard read can also contain an earlier receipt. */
export function publishReceipt(result: unknown): void {
  if (result && typeof result === "object" && "receipt_id" in result && typeof result.receipt_id === "number") {
    window.dispatchEvent(new CustomEvent("ta:write-receipt", { detail: result.receipt_id }));
  }
}
